import { useContext, useState } from 'react';

import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Typography } from '@mui/material';
import { processApiPath } from '../../utils/url';
import { RECORDING_DELETE } from '../../config/apiPaths';
import { ClassContext } from '../../context/ClassContext';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { DeleteConfirmationDialog } from '../common/dialog/DeleteConfirmationDialog';
import { RecordingStatus } from '../../types/recordingTypes';
import { RecordingDialogProps } from './types';

export const DeleteRecordingDialog = (props: RecordingDialogProps) => {
    const { t } = useTranslation();
    const { open, recording, recordings, setRecordings, handleDialogClosed } = props;
    const { classUuid, userUuid } = useContext(ClassContext);
    const { data: schoolInfo } = useSchoolInfo();
    const schoolUuid = schoolInfo?.uuid;
    const [confirming, setConfirming] = useState(false);

    const handleDialogConfirmed = async () => {
        if (!schoolUuid || !classUuid || !recording?.recordingId) {
            console.error('Missing required data to delete recording');
            handleDialogClosed();
            return;
        }

        const doDelete = async () => {
            const url = processApiPath(
                RECORDING_DELETE,
                {
                    schoolUuid,
                    classUuid,
                    recordingId: recording.recordingId
                },
                { action: 'SOFT' }
            );
            setConfirming(true);
            try {
                await axios.delete(url, {
                    headers: {
                        user_uuid: userUuid
                    }
                });
            } catch (e) {
                console.error(e);
                // TODO show error here.
            } finally {
                setConfirming(false);
            }
        };
        await doDelete();

        // Mark the recording deleted.
        const newRecordings = recordings.map((r) => {
            if (r.recordingId === recording?.recordingId) {
                r.status = RecordingStatus.DELETED;
            }
            return r;
        });

        setRecordings(newRecordings);
        handleDialogClosed();
    };

    return (
        <DeleteConfirmationDialog
            id="delete-recording"
            open={open}
            title={t('lti:recordings.delete-title')}
            confirmLabel={t('common:delete')}
            onClose={handleDialogClosed}
            onConfirm={handleDialogConfirmed}
            confirming={confirming}
            setConfirming={setConfirming}
        >
            <Typography>
                {t('lti:recordings.delete-confirmation', {
                    name: recording?.name ?? '',
                    interpolation: { escapeValue: false } // Already escaped.
                })}
            </Typography>
        </DeleteConfirmationDialog>
    );
};
